/**
 * Service-worker registration (issue 312).
 *
 * Kept in `config/` beside `theme.js` and `typography.js` — the modules that
 * own one piece of device state each — because that is what this is: whether
 * this device has an offline copy.
 *
 * WHEN IT REGISTERS. Production builds only, over HTTPS or on localhost, and
 * only where the browser has `serviceWorker`. A development build must not
 * register one: CRA's dev server serves unhashed assets, and a cache-first
 * worker in front of them is how a developer ends up debugging yesterday's
 * bundle.
 *
 * WHY REGISTRATION IS DEFERRED TO `load`. The registration fetch competes with
 * the page's own assets for the first paint it is supposed to make faster.
 *
 * THE SIGN-OUT SWEEP. `clearOfflineCaches()` messages the worker to drop every
 * cache it owns. The worker never stores an authenticated response (see its
 * header), so this is not strictly load-bearing for privacy — but "the last
 * reader's pages are still on this machine" is a surprising thing to leave on
 * a shared device, and the sweep costs one message.
 */

const SW_URL = '/service-worker.js';

const isLocalhost = () => typeof window !== 'undefined' && Boolean(
  window.location.hostname === 'localhost'
  || window.location.hostname === '[::1]'
  || /^127(?:\.\d{1,3}){3}$/.test(window.location.hostname),
);

export function supported() {
  return typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined'
    && (window.location.protocol === 'https:' || isLocalhost());
}

export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production') return;
  if (!supported()) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(SW_URL).catch(() => {
      // A failed registration is not a failed page. The site works exactly as
      // it did before offline support existed, which is the whole point of
      // treating this as an enhancement.
    });
  });
}

export function clearOfflineCaches() {
  if (!supported()) return;
  const active = navigator.serviceWorker.controller;
  if (active) active.postMessage({ type: 'CLEAR_CACHES' });
}

export default { registerServiceWorker, clearOfflineCaches, supported };
